/**
 * TTSGenerator - Text-to-Speech Generator using Amazon Polly
 * 
 * Admin tool to generate speech from text in multiple languages
 */

import React, { useState, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import './TTSGenerator.css';

// Available Polly voices by language
const POLLY_VOICES = {
  'yue-CN': {
    name: 'Cantonese',
    voices: [
      { id: 'Hiujin', name: 'Hiujin (Female)', gender: 'Female', neural: true }
    ]
  },
  'cmn-CN': {
    name: 'Mandarin Chinese',
    voices: [
      { id: 'Zhiyu', name: 'Zhiyu (Female)', gender: 'Female', neural: false }
    ]
  },
  'en-US': {
    name: 'English (US)',
    voices: [
      { id: 'Joanna', name: 'Joanna (Female)', gender: 'Female', neural: true },
      { id: 'Matthew', name: 'Matthew (Male)', gender: 'Male', neural: true },
      { id: 'Amy', name: 'Amy (Female)', gender: 'Female', neural: true },
      { id: 'Justin', name: 'Justin (Male)', gender: 'Male', neural: true }
    ]
  },
  'en-GB': {
    name: 'English (British)',
    voices: [
      { id: 'Amy', name: 'Amy (Female)', gender: 'Female', neural: true },
      { id: 'Brian', name: 'Brian (Male)', gender: 'Male', neural: true },
      { id: 'Emma', name: 'Emma (Female)', gender: 'Female', neural: true }
    ]
  }
};

// TTS API endpoint - uses existing AI Config Lambda with /tts route
const TTS_API_URL = 'https://q73liawz5kskogsmyokmtrqrni0meydu.lambda-url.ap-southeast-1.on.aws';

const TTSGenerator = () => {
  const audioRef = useRef(null);
  const [text, setText] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('yue-CN');
  const [selectedVoice, setSelectedVoice] = useState('Hiujin');
  const [isGenerating, setIsGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [generationInfo, setGenerationInfo] = useState(null);

  // Get current language's voices
  const currentLanguageVoices = POLLY_VOICES[selectedLanguage]?.voices || [];

  // Handle language change
  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setSelectedLanguage(newLang);
    // Auto-select first voice for the new language
    const voices = POLLY_VOICES[newLang]?.voices || [];
    if (voices.length > 0) {
      setSelectedVoice(voices[0].id);
    }
    // Clear previous audio
    setAudioUrl(null);
    setGenerationInfo(null);
  };

  // Handle voice change
  const handleVoiceChange = (e) => {
    setSelectedVoice(e.target.value);
    // Clear previous audio
    setAudioUrl(null);
    setGenerationInfo(null);
  };

  // Get auth token
  const getAuthToken = async () => {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || '';
  };

  // Generate speech
  const handleGenerate = async () => {
    if (!text.trim()) {
      setError('Please enter some text to convert to speech.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setAudioUrl(null);
    setGenerationInfo(null);

    try {
      const token = await getAuthToken();
      
      // Get the selected voice details
      const voiceDetails = currentLanguageVoices.find(v => v.id === selectedVoice);
      
      const response = await fetch(`${TTS_API_URL}/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text: text.trim(),
          voiceId: selectedVoice,
          languageCode: selectedLanguage,
          engine: voiceDetails?.neural ? 'neural' : 'standard'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success || result.error) {
        throw new Error(result.error || 'Failed to generate speech');
      }

      setAudioUrl(result.audioUrl);
      setGenerationInfo({
        language: POLLY_VOICES[selectedLanguage]?.name,
        voice: voiceDetails?.name || selectedVoice,
        characters: text.length
      });
    } catch (err) {
      console.error('[TTSGenerator] Error:', err);
      setError(err.message || 'Failed to generate speech. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Download audio
  const handleDownload = () => {
    if (!audioUrl) return;
    
    const link = document.createElement('a');
    link.href = audioUrl;
    link.download = `tts-${selectedVoice}-${Date.now()}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Clear all
  const handleClear = () => {
    setText('');
    setAudioUrl(null);
    setError(null);
    setGenerationInfo(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
  };

  return (
    <div className="tts-generator">
      <div className="tts-generator__header">
        <h2 className="tts-generator__title">🎤 Text-to-Speech Generator</h2>
        <p className="tts-generator__subtitle">
          Generate speech from text using Amazon Polly
        </p>
      </div>

      <div className="tts-generator__content">
        {/* Language & Voice Selection */}
        <div className="tts-generator__controls">
          <div className="tts-generator__control">
            <label className="tts-generator__label">Language</label>
            <select
              className="tts-generator__select"
              value={selectedLanguage}
              onChange={handleLanguageChange}
            >
              {Object.entries(POLLY_VOICES).map(([code, lang]) => (
                <option key={code} value={code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className="tts-generator__control">
            <label className="tts-generator__label">Voice</label>
            <select
              className="tts-generator__select"
              value={selectedVoice}
              onChange={handleVoiceChange}
            >
              {currentLanguageVoices.map((voice) => (
                <option key={voice.id} value={voice.id}>
                  {voice.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Text Input */}
        <div className="tts-generator__input-section">
          <label className="tts-generator__label">Text to Convert</label>
          <textarea
            className="tts-generator__textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter text to convert to speech..."
            rows={10}
          />
          <div className="tts-generator__char-count">
            {text.length} characters
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="tts-generator__error">
            <span className="tts-generator__error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="tts-generator__actions">
          <button
            className="tts-generator__button tts-generator__button--primary"
            onClick={handleGenerate}
            disabled={isGenerating || !text.trim()}
          >
            {isGenerating ? (
              <>
                <span className="tts-generator__spinner"></span>
                Generating...
              </>
            ) : (
              <>
                <span className="tts-generator__button-icon">🔊</span>
                Generate Speech
              </>
            )}
          </button>

          <button
            className="tts-generator__button tts-generator__button--secondary"
            onClick={handleClear}
            disabled={isGenerating}
          >
            <span className="tts-generator__button-icon">🗑️</span>
            Clear
          </button>
        </div>

        {/* Audio Preview & Download */}
        {audioUrl && (
          <div className="tts-generator__result">
            <h3 className="tts-generator__result-title">Audio Preview</h3>
            
            {generationInfo && (
              <div className="tts-generator__info">
                <span className="tts-generator__info-item">
                  <strong>Language:</strong> {generationInfo.language}
                </span>
                <span className="tts-generator__info-item">
                  <strong>Voice:</strong> {generationInfo.voice}
                </span>
                <span className="tts-generator__info-item">
                  <strong>Characters:</strong> {generationInfo.characters}
                </span>
              </div>
            )}

            <audio
              ref={audioRef}
              className="tts-generator__audio"
              controls
              src={audioUrl}
            >
              Your browser does not support the audio element.
            </audio>

            <button
              className="tts-generator__button tts-generator__button--download"
              onClick={handleDownload}
            >
              <span className="tts-generator__button-icon">📥</span>
              Download MP3
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TTSGenerator;
